"use client";
import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';

interface ServiceItem {
    title: string;
    href?: string;
    icon: React.ReactNode;
    desc: string;
}

const services: ServiceItem[] = [
    {
        title: 'Google Ads Management',
        href: '/services/performance-marketing/google-ads',
        icon: (
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <circle cx="13" cy="13" r="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M18.5 18.5L26 26" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                <path d="M10 13h6M13 10v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        ),
        desc: 'Capture high-intent customers through Search, Shopping, Performance Max, and YouTube campaigns designed to maximise conversions and ROAS.',
    },
    {
        title: 'Meta Ads Management',
        href: '/services/performance-marketing/meta-ads',
        icon: (
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M4 20c0-4.5 2-8 5-8 2 0 3.5 2 5 5 1.5-3.5 3-5 5-5 3 0 5 3.5 5 8 0 2-1 4-3 4s-3-2-4-4c-1 2-2 4-4 4s-4-2-4-4z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
            </svg>
        ),
        desc: 'Create Facebook and Instagram campaigns that turn audience attention into qualified leads, enquiries, and sales through behaviour-led creative.',
    },
    {
        title: 'LinkedIn Ads',
        href: '/services/performance-marketing/linkedin-ads',
        icon: (
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <rect x="4" y="4" width="24" height="24" rx="5" stroke="currentColor" strokeWidth="2"/>
                <path d="M10 14v8M10 10v1" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                <path d="M15 22v-5c0-2 1.5-3 3-3s3 1 3 3v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15 14v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        ),
        desc: 'Reach decision-makers through targeted B2B campaigns, lead generation systems, and account-based marketing strategies.',
    },
    {
        title: 'Conversion Rate Optimisation',
        href: '/services/performance-marketing/conversion-rate-optimization',
        icon: (
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M6 22l6-7 5 4 9-11" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 10h4v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
        desc: 'Increase the value of every click by improving landing pages, user journeys, messaging, and conversion pathways across the full funnel.',
    },
    {
        title: 'Analytics & Attribution',
        href: '/services/performance-marketing/marketing-analytics',
        icon: (
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <rect x="5" y="18" width="5" height="9" rx="1.5" stroke="currentColor" strokeWidth="2"/>
                <rect x="13" y="12" width="5" height="15" rx="1.5" stroke="currentColor" strokeWidth="2"/>
                <rect x="21" y="6" width="5" height="21" rx="1.5" stroke="currentColor" strokeWidth="2"/>
            </svg>
        ),
        desc: 'Gain complete visibility with GA4, Google Tag Manager, server-side tracking, Meta Conversion API, and advanced attribution systems.',
    },
];

function ServiceCard({ service, index }: { service: ServiceItem; index: number }) {
    const num = String(index + 1).padStart(2, '0');

    const card = (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: index * 0.09 }}
            className="relative overflow-hidden rounded-3xl p-8 flex flex-col gap-6 h-full"
            style={{
                minHeight: 280,
                background: '#ffffff',
                border: '1.5px solid rgba(0,0,0,0.08)',
            }}
        >
            {/* ghost index number */}
            <span
                className="absolute top-6 right-7 select-none pointer-events-none"
                style={{
                    fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                    fontSize: 56,
                    lineHeight: 1,
                    color: 'rgba(8,4,0,0.05)',
                }}
            >
                {num}
            </span>

            {/* Icon */}
            <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 relative z-10"
                style={{
                    background: 'rgba(55,48,163,0.08)',
                    border: '1px solid rgba(55,48,163,0.16)',
                    color: '#3730a3',
                }}
            >
                {service.icon}
            </div>

            {/* Title + description */}
            <div className="flex flex-col gap-2.5 relative z-10">
                <h3
                    className="text-xl md:text-2xl font-bold leading-snug"
                    style={{
                        fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                        color: '#080400',
                        letterSpacing: '0.01em',
                    }}
                >
                    {service.title}
                </h3>
                <p
                    className="text-sm leading-relaxed"
                    style={{
                        fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                        color: 'rgba(8,4,0,0.55)',
                        letterSpacing: '0.01em',
                    }}
                >
                    {service.desc}
                </p>
            </div>

            {/* Learn more button */}
            <div className="mt-auto pt-2 relative z-10">
                <span
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-[0.1em] transition-colors duration-200"
                    style={{
                        background: 'rgba(55,48,163,0.06)',
                        border: '1px solid rgba(55,48,163,0.18)',
                        color: '#3730a3',
                        fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                    }}
                >
                    Learn More
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                        <path d="M2 6.5h9M7.5 2.5l4 4-4 4" stroke="#3730a3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </span>
            </div>
        </motion.div>
    );

    if (service.href) {
        return (
            <Link href={service.href} className="block h-full">
                {card}
            </Link>
        );
    }

    return card;
}

export default function PerformanceDeliverables() {
    return (
        <section
            className="relative overflow-hidden py-24 px-6 md:px-16 lg:px-24"
            style={{ background: '#080400' }}
        >
            <div className="max-w-[1400px] mx-auto flex flex-col gap-16">

                {/* Header */}
                <div className="flex flex-col gap-4">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: '#ceff1a', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                    >
                        Our Services
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-4xl md:text-6xl font-bold tracking-tight leading-tight"
                        style={{
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            color: '#fbfdfd',
                        }}
                    >
                        OUR SERVICES
                    </motion.h2>
                </div>

                {/* Card layout — wraps and centers the final, incomplete row */}
                <div className="flex flex-wrap justify-center gap-5">
                    {services.map((s, i) => (
                        <div
                            key={i}
                            className="w-full md:w-[calc(50%-10px)] lg:w-[calc(33.333%-13.334px)]"
                        >
                            <ServiceCard service={s} index={i} />
                        </div>
                    ))}
                </div>

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-center"
                >
                    <Link
                        href="/contact"
                        className="inline-flex items-center justify-center px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                        style={{
                            background: '#3730a3',
                            color: '#ffffff',
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            letterSpacing: '0.08em',
                        }}
                    >
                        Build My Custom Growth Plan →
                    </Link>
                </motion.div>
            </div>
        </section>
    );
}
